import React, { useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { JobStatus } from 'components/StatusTag/StatusTag';
import {
  STATUS_CONFIG,
  Section,
  formatDate,
  Row,
  formatDuration,
  LANG_STYLES,
  CopyableValue,
  formatMemory,
} from 'components/JobsTable/JobsDetailsSidebar/JobDetailsSidebarHelpers';
import { useGetJobDetail } from 'hooks/jobs';
import { JobPropertiesContent } from 'components/JobsTable/JobsDetailsSidebar/Modals/Contents/JobPropertiesContent';
import { JobCodeContent } from 'components/JobsTable/JobsDetailsSidebar/Modals/Contents/JobCodeContent';
import { JobStepState, JobStep, JobState } from 'types/types';
import './JobDetailsPage.scss';

type PanelTab = 'properties' | 'code';

export function JobDetailsPage() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id') ?? '';
  const prefix = searchParams.get('prefix') ?? '';
  const expires = searchParams.get('expires') ?? '';
  const signature = searchParams.get('signature') ?? '';
  const schemaName = searchParams.get('schema') ?? '';
  const { clusterName } = useParams<{ clusterName: string }>();

  const isSignedURL = !!expires && !!signature;

  const [activeTab, setActiveTab] = useState<PanelTab>('properties');

  const { data: jobDetails, isLoading } = useGetJobDetail({
    jobId: jobId ?? '',
    schemaName: schemaName ?? '',
    isEnabled: !!jobId,
    isSignedURL,
    expires,
    signature,
    prefix,
    clusterName,
  });

  const statusCfg = jobDetails
    ? STATUS_CONFIG[jobDetails.sparkJobStatus as unknown as JobStatus]
    : null;

  return (
    <div className="job-details-page">
      <div className="job-details-page__left">
        <div className="job-details-page__header">
          <div className="job-details-page__header-meta">
            <span className="job-details-page__eyebrow">Job Details</span>
            {isLoading ? (
              <Skeleton.Input active size="small" style={{ width: 260 }} />
            ) : (
              <h1 className="job-details-page__title">
                {jobDetails?.sparkJobId ?? jobId ?? '—'}
              </h1>
            )}
          </div>
          {!isLoading && statusCfg && (
            <span
              className="job-details-page__status-pill"
              style={{ color: statusCfg.color, background: statusCfg.bg }}>
              {statusCfg.icon}
              <span style={{ marginLeft: 6 }}>{statusCfg.label}</span>
            </span>
          )}
        </div>

        {/* Detail sections */}
        <div className="job-details-page__sections">
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 12 }} />
          ) : jobDetails ? (
            <>
              <Section title="Identity">
                <Row label="Job ID">
                  <CopyableValue value={jobDetails.sparkJobId} />
                </Row>
                <Row label="App ID">
                  <CopyableValue value={jobDetails.sparkAppId} />
                </Row>
                <Row label="Table">
                  <span className="job-details-page__mono">
                    {jobDetails.schemaName}.{jobDetails.tableName}
                  </span>
                </Row>
                <Row label="Schema">
                  <span className="job-details-page__mono">
                    {jobDetails.schemaName}
                  </span>
                </Row>
                <Row label="Cluster">
                  <span className="job-details-page__mono">
                    {jobDetails.clusterName}
                  </span>
                </Row>
                <Row label="Cluster Prefix / URL">
                  <span className="job-details-page__mono">
                    {jobDetails.clusterPrefix} / {jobDetails.clusterUrl}
                  </span>
                </Row>
                <Row label="Vendor">{jobDetails.vendor}</Row>
              </Section>

              <div className="job-details-page__divider" />

              <Section title="Timing">
                <Row label="Created">{formatDate(jobDetails.createdAt)}</Row>
                <Row label="Updated">
                  {formatDate(jobDetails?.updatedAt ?? null)}
                </Row>
                <Row label="Dequeued">
                  {formatDate(jobDetails?.dequeuedAt ?? null)}
                </Row>
                <Row label="Duration">
                  <span className="job-details-page__duration">
                    {formatDuration(jobDetails.duration)}
                  </span>
                </Row>
              </Section>

              <div className="job-details-page__divider" />

              <Section title="Resources">
                <Row label="Language">
                  <span
                    className="job-details-page__lang-pill"
                    style={LANG_STYLES[jobDetails.mvLanguage] || {}}>
                    {jobDetails?.mvLanguage ?? '—'}
                  </span>
                </Row>
                <Row label="Executors">{jobDetails.totalExecutorCount}</Row>
                <Row label="Peak memory">
                  {formatMemory(jobDetails.peakMemoryInMB)}
                </Row>
                <Row label="Driver memory">
                  {formatMemory(jobDetails.sparkDriverMemoryInMB)}
                </Row>
              </Section>

              <div className="job-details-page__divider" />

              <Section title="ICDB data">
                <Row label="Job State">
                  {JobState[Number(jobDetails.jobState)]}
                </Row>
                <Row label="Extracted Row Count">
                  {jobDetails.extractedRowCount?.toLocaleString() ?? '—'}
                </Row>
                <Row label="Rejected Row Count">
                  {jobDetails.rejectedRowCount?.toLocaleString() ?? '—'}
                </Row>
                <Row label="Loaded Row Count">
                  {jobDetails.loadedRowCount?.toLocaleString() ?? '—'}
                </Row>
                {jobDetails.jobStates && jobDetails.jobStates.length > 0 && (
                  <Row label="Job States:">
                    <div className="job-details-sidebar__job-states">
                      {jobDetails.jobStates.map(
                        (state: { stateId: number; stateResult: string }) => (
                          <div
                            key={state.stateId}
                            className="job-details-sidebar__job-state-item">
                            <span className="job-details-sidebar__mono">
                              {JobStep[state.stateId]} :{' '}
                              {JobStepState[Number(state.stateResult)]}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </Row>
                )}
              </Section>
            </>
          ) : (
            <div className="job-details-page__empty">
              No job data available.
            </div>
          )}
        </div>
      </div>
      <div className="job-details-page__right">
        <div className="job-details-page__tab-group" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'properties'}
            className={[
              'job-details-page__tab',
              activeTab === 'properties' && 'job-details-page__tab--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab('properties')}>
            Properties
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'code'}
            className={[
              'job-details-page__tab',
              activeTab === 'code' && 'job-details-page__tab--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab('code')}>
            Code
          </button>
        </div>

        <div className="job-details-page__panel-content">
          {activeTab === 'properties' ? (
            <JobPropertiesContent
              job={jobDetails}
              isLoading={isLoading}
              height="915px"
            />
          ) : (
            <JobCodeContent
              job={jobDetails}
              isLoading={isLoading}
              style={{
                fontSize: '14px',
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                gap: 0,
                height: '820px',
                overflow: 'auto',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default JobDetailsPage;
